import React from 'react';
import { Alert, Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-core';

export interface DeleteDocumentModalProps {
  /** Whether the modal is open. */
  visible: boolean;
  /** Name of the document being deleted, shown in the confirmation body. */
  fileName: string;
  /** Whether the delete request is currently in flight (disables the control). */
  isDeleting: boolean;
  /** Inline error from a failed delete attempt (null when none) — the modal stays open for retry. */
  error: string | null;
  /** Confirms and performs the delete. */
  onConfirm: () => void;
  onDismiss: () => void;
}

/**
 * Confirmation modal for deleting a single uploaded document. Delete is destructive and not
 * easily undone, so the user must confirm the named file before the delete runs. The delete
 * side-effect lives in the parent's {@link useWorkDocsDelete} hook; this component is
 * presentation-only.
 */
export const DeleteDocumentModal: React.FC<DeleteDocumentModalProps> = ({ visible, fileName, isDeleting, error, onConfirm, onDismiss }) => (
  <Modal
    visible={visible}
    onDismiss={onDismiss}
    header="Delete Document"
    footer={
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onDismiss} disabled={isDeleting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={isDeleting} disabled={isDeleting}>
            Delete
          </Button>
        </SpaceBetween>
      </Box>
    }
  >
    <SpaceBetween size="s">
      <Box variant="span">
        Delete <Box variant="strong">{fileName}</Box>? You can&apos;t undo this action.
      </Box>
      {error && <Alert type="error">{error}</Alert>}
    </SpaceBetween>
  </Modal>
);
